<script setup lang="ts">
  import { ref, computed, onMounted, onBeforeMount } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { toPolicyTimestamp } from '@qicgroupdoha/travel-devkit/helpers'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'
  import { useUserStore, useCommonStore } from '@/shared/store'
  import { usePerformanceTiming, useValidation, useValidators } from '@/shared/composables'
  import {
    MAX_DEPARTURE_DAY_WITHOUT_TODAY,
    MAX_DEPARTURE_DAY_WITHOUT_TODAY_OUTBOUND,
    MAX_DEPARTURE_DAY_WITHOUT_TODAY_USA,
    PROCEED_EVENT_NAME,
    LOAD_EVENT_NAME,
  } from './FlowTravelDatesWidgetQC.constants'
  import { formatDateDefault, getDateWithoutTime } from '@/shared/utils'
  import { TravelType } from '@/shared/types/api.types'
  import { TravelDirection, type Locale } from '@/shared/types/common.types'
  import { AnalyticsPageName } from '@/shared/constants/analytics.constants'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { OUTBOUND_MULTI_TRIP_RETURN_DAY } from '@/shared/constants/plans.constants'
  import TabsQC from '@qicgroupdoha/qic-connect-ds/components/TabsQC'
  import InputDateQC from '@qicgroupdoha/qic-connect-ds/components/InputDateQC'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (
      e: typeof LOAD_EVENT_NAME,
      request: () => Promise<any>,
      requestId?: string,
      withLoader?: boolean
    ): void
    (e: 'back'): void
  }

  interface Props {
    typeOfTravel?: Exclude<TravelDirection, TravelDirection.UNKNOWN>
    withBackButton?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    typeOfTravel: TravelDirection.OUTBOUND,
    withBackButton: false,
  })

  const emit = defineEmits<Emits>()

  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const { t, locale } = useI18n()
  const gaService = useGoogleAnalytics()
  const { onPageReady, onPerformanceCheckEnd } = usePerformanceTiming()
  const isAllWorld = !!userStore.destination.find((el) => el.id === '999')
  const isUsaOrCanada = userStore.destination.some((el) => ['082', '020'].includes(el.id))
  const isInboud = props.typeOfTravel === TravelDirection.INBOUND

  const tabs = [
    {
      label: t('outbound_travel_type_single'),
      caption: t(
        isAllWorld || isUsaOrCanada
          ? 'outbound_travel_type_single_desc_60'
          : 'outbound_travel_type_single_desc'
      ),
      id: 'tab1',
    },
    {
      label: t('outbound_travel_type_multi'),
      caption: t('outbound_travel_type_multi_desc'),
      id: 'tab2',
    },
  ]

  const popupAnnotation = t(
    (isAllWorld || isUsaOrCanada) && !isInboud
      ? 'outbound.calendar.trip_length_60'
      : 'outbound.calendar.trip_length'
  )

  const activeTab = ref(userStore.tripType === 'SINGLE' ? tabs[0].id : tabs[1].id)
  const departureDate = ref<Date | null>(userStore.departureDate)
  const returnDate = ref<Date | null>(userStore.returnDate)
  const lastDepartureDateMulti = ref<Date | null>(null)
  const lastDepartureDateSingle = ref<Date | null>(null)
  const lastReturnDateSingle = ref<Date | null>(null)
  const isLoading = ref(false)

  const setReturnDateForMultiTrip = (departureDate: Date) => {
    const departureDateValue = new Date(departureDate)
    return new Date(
      departureDateValue.setDate(departureDateValue.getDate() + OUTBOUND_MULTI_TRIP_RETURN_DAY)
    )
  }

  const tripDates = computed({
    get: () => {
      if (isSingleTrip.value) {
        return departureDate.value && returnDate.value
          ? [departureDate.value, returnDate.value]
          : null
      }
      return departureDate.value ?? null
    },
    set: (dates: [Date | null, Date | null] | Date | null) => {
      if (isSingleTrip.value && Array.isArray(dates)) {
        departureDate.value = dates[0] || null
        returnDate.value = dates[1] || null
        lastDepartureDateSingle.value = departureDate.value
        lastReturnDateSingle.value = returnDate.value
      } else if (dates instanceof Date) {
        departureDate.value = dates ?? null
        if (departureDate.value) {
          lastDepartureDateMulti.value = departureDate.value
          returnDate.value = setReturnDateForMultiTrip(departureDate.value)
        }
      }
    },
  })

  const isSingleTrip = computed(() => isInboud || activeTab.value === tabs[0].id)

  const { required } = useValidators()
  const validationRules = computed(() => ({
    departureDate: {
      required,
    },
    returnDate: {
      required,
    },
  }))

  const { v$, errors } = useValidation(validationRules, {
    departureDate,
    returnDate,
  })

  const minDepartureDate = new Date()
  const focusedDate = ref<{ start: Date; end: Date } | null>(null)

  const maxDays = computed(() => {
    let maxDaysCount = isInboud
      ? MAX_DEPARTURE_DAY_WITHOUT_TODAY
      : MAX_DEPARTURE_DAY_WITHOUT_TODAY_OUTBOUND
    const focusedStartDate = focusedDate.value?.start
    if (focusedStartDate) {
      maxDaysCount = MAX_DEPARTURE_DAY_WITHOUT_TODAY
      if (!isInboud && (isAllWorld || isUsaOrCanada)) {
        maxDaysCount = MAX_DEPARTURE_DAY_WITHOUT_TODAY_USA
      }
    }
    return maxDaysCount
  })

  const minDate = computed(() => {
    const start = focusedDate.value?.start
    const end = focusedDate.value?.end ? new Date(focusedDate.value.end) : null
    let focusedStartDate

    if (start && end && start < end) {
      const dateMinus90Days = new Date(end.setDate(end.getDate() - maxDays.value))
      if (dateMinus90Days > minDepartureDate) {
        focusedStartDate = dateMinus90Days
      }
    }
    return focusedStartDate || minDepartureDate
  })

  const maxDate = computed(() => {
    const focusedStartDate = focusedDate.value?.start ? new Date(focusedDate.value.start) : null
    if (focusedStartDate) {
      return new Date(focusedStartDate.setDate(focusedStartDate.getDate() + maxDays.value))
    }

    const today = new Date()
    return new Date(today.setDate(today.getDate() + maxDays.value))
  })

  const multitripAnnotation = computed(() => {
    if (!isSingleTrip.value && departureDate.value && returnDate.value) {
      return `${t('from')} ${formatDateDefault(departureDate.value, locale.value as Locale)} ${t('till')} ${formatDateDefault(
        returnDate.value,
        locale.value as Locale
      )}`
    }
    return null
  })

  const handleTabChange = () => {
    if (isSingleTrip.value) {
      departureDate.value = lastDepartureDateSingle.value ?? null
      returnDate.value = lastReturnDateSingle.value ?? null
    } else {
      lastDepartureDateMulti.value ??= departureDate.value ?? null
      departureDate.value = lastDepartureDateMulti.value
      returnDate.value = departureDate.value ? setReturnDateForMultiTrip(departureDate.value) : null
    }
  }

  const onProceed = async () => {
    v$.value.$touch()
    if (!v$.value.$error) {
      emit(
        LOAD_EVENT_NAME,
        async () => {
          if (departureDate.value && returnDate.value) {
            isLoading.value = true
            userStore.setTravelDates(departureDate.value, returnDate.value, true)

            const tripType = isSingleTrip.value ? 'SINGLE' : 'MULTI'
            userStore.setTripType(tripType, true)
            userStore.setPrevTripType(tripType, true)

            const data: {
              policyDate: number
              endDate: number
              travelType: TravelType
              originCode?: string
              originName?: string
            } = {
              policyDate: toPolicyTimestamp(departureDate.value), // Send as unix timestamp
              endDate: toPolicyTimestamp(returnDate.value), // Send as unix timestamp
              travelType: isSingleTrip.value ? TravelType.SINGLE : TravelType.MULTITRIP,
            }

            if (isInboud && userStore.residenceCountry) {
              data.originCode = userStore.residenceCountry.id
              data.originName = userStore.residenceCountry.name
            }

            await commonStore.sendFormData(data)
            gaService.sendTravelDatesProceed(
              props.typeOfTravel,
              isSingleTrip.value ? 'single' : 'multi',
              departureDate.value,
              returnDate.value,
              userStore.destination?.map((country) => country?.id || '')
            )

            emit(PROCEED_EVENT_NAME)

            isLoading.value = false
          }
        },
        'sendFormData',
        false
      )
    }
  }

  onBeforeMount(() => {
    if (departureDate.value && returnDate.value) {
      if (
        getDateWithoutTime(new Date(departureDate.value)).getTime() >=
        getDateWithoutTime(new Date()).getTime()
      ) {
        if (isSingleTrip.value && userStore.tripType === 'SINGLE') {
          lastDepartureDateSingle.value = departureDate.value
          lastReturnDateSingle.value = returnDate.value
        } else if (userStore.tripType === 'MULTI') {
          lastDepartureDateMulti.value = departureDate.value
        } else {
          departureDate.value = null
          returnDate.value = null
        }
      } else {
        departureDate.value = null
        returnDate.value = null
      }
    }
  })

  onMounted(() => {
    onPageReady()
    onPerformanceCheckEnd()
    gaService.sendViewPage(props.typeOfTravel, AnalyticsPageName.TRAVEL_DATES)
  })
</script>

<template>
  <FlowCardQC
    :title="t('outbound_travel_type_header')"
    with-button
    :is-loading="isLoading"
    :with-back-button="withBackButton"
    @button-click="onProceed"
    @back="emit('back')"
  >
    <TabsQC
      v-if="!isInboud"
      v-model="activeTab"
      :tabs="tabs"
      variant="solid"
      size="medium"
      data-test="travel-dates-tabs"
      :style="{ pointerEvents: isLoading ? 'none' : 'initial' }"
      @update:model-value="handleTabChange"
    />

    <WrapperQC direction="column" gap="var(--spacing-5)">
      <!-- @vue-ignore -->
      <InputDateQC
        v-model="tripDates"
        data-test="travel-dates-single-trip"
        :label="t('outbound.calendar.first_departure')"
        :calendar-props="{
          range: isSingleTrip,
          multiCalendars: $qicConnect?.isMobile || !isSingleTrip ? false : true,
          disableMonthYearSelect: true,
          preventMinMaxNavigation: false,
          width: isSingleTrip ? 740 : 480,
        }"
        :range-config="{
          startLabel: $t('inbound_calendar_departure'),
          endLabel: $t('inbound_calendar_return'),
          mobileTitle: $t('outbound_travel_type_header'),
          gap: 'var(--spacing-3)',
          errorsStart: errors.departureDate,
          errorsEnd: errors.returnDate,
          autoSetFocusedDates: true,
        }"
        :input-props="{
          loading: isLoading,
        }"
        :errors="errors.departureDate"
        :locale="locale"
        :max-date="maxDate"
        :min-date="minDate"
        @day-focus="focusedDate = $event"
        @close="focusedDate = null"
      >
        <template #calendar-footer>
          <p class="input-date-footer text-center text-medium">
            {{ popupAnnotation }}
          </p>
        </template>
      </InputDateQC>

      <div v-if="multitripAnnotation">
        <p class="text-center text-regular">{{ multitripAnnotation }}</p>
        <p class="text-center text-regular">{{ popupAnnotation }}</p>
      </div>
    </WrapperQC>
  </FlowCardQC>
</template>

<style lang="scss" scoped>
  .input-date-footer {
    padding-block: var(--spacing-5);
  }
</style>
